// per docs/shape-ops-agreed-alerts.md §2.10
// per specs/057-alert-query-builder/plan.md §D5 (AGE components only)
// per docs/caiet-uc-spec.md §UC-ALRT-02 §UC27-01
//
// The builder condition of an alert rule: which sources it reads, what it measures and
// how, what it groups on, what it filters, over which window, and against which
// threshold. Every choice comes from the catalogue the backend serves, so a field the
// compiler does not know is never offered.
import {
  MudButton,
  MudCheckbox,
  MudChip,
  MudIcon,
  MudTextInput,
  MudSelect,
} from '@egov-moldova/mud-react';
import { useTranslate } from '@tolgee/react';
import { ALERT_CARRIED_SOURCES, alertReadSources } from '@simsm/shared-types';
import { FormField, FormRow } from '../../components/FormPage/FormPage';
import {
  LIST_OPERATORS,
  builderErrors,
  kindOf,
  valueLabelKey,
  type BuilderCatalog,
  type BuilderFilterForm,
  type BuilderForm,
} from './alert-builder';

interface Option {
  value: string;
  label: string;
}

interface Props {
  form: BuilderForm;
  catalog: BuilderCatalog | undefined;
  submitted: boolean;
  onChange: (next: BuilderForm) => void;
}

interface DimensionFieldProps {
  label: string;
  hint: string;
  placeholder: string;
  options: Option[];
  selected: string[];
  max: number;
  onChange: (next: string[]) => void;
}

// AGE has no multi-select atom: the select offers what is not chosen yet and each choice
// becomes a removable chip, the composition of StockChartTable's FilterPanel.
function DimensionField({
  label,
  hint,
  placeholder,
  options,
  selected,
  max,
  onChange,
}: DimensionFieldProps): JSX.Element {
  const labelOf = (value: string): string =>
    options.find((option) => option.value === value)?.label ?? value;
  const remaining = options.filter((option) => !selected.includes(option.value));
  return (
    <fieldset className="simsm-form-page__fieldset">
      <legend className="simsm-form-page__field-label">{label}</legend>
      <MudSelect
        size="medium"
        value=""
        aria-label={label}
        placeholder={placeholder}
        disabled={remaining.length === 0 || selected.length >= max}
        onMudChange={(e: CustomEvent<{ value: string }>) => {
          if (e.detail.value) onChange([...selected, e.detail.value]);
        }}
      >
        {remaining.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </MudSelect>
      {selected.length > 0 ? (
        <ul className="simsm-medmulti__chips simsm-alerts__label-chips">
          {selected.map((value) => (
            <li key={value}>
              <MudChip
                type="input"
                size="sm"
                removable
                onMudRemove={() => onChange(selected.filter((item) => item !== value))}
              >
                {labelOf(value)}
              </MudChip>
            </li>
          ))}
        </ul>
      ) : null}
      <span className="simsm-alerts__meta">{hint}</span>
    </fieldset>
  );
}

export function AlertBuilderEditor({ form, catalog, submitted, onChange }: Props): JSX.Element {
  const { t } = useTranslate();
  const errors = submitted ? builderErrors(form) : {};
  const set = (patch: Partial<BuilderForm>): void => onChange({ ...form, ...patch });

  const fieldLabel = (id: string): string => t(`alerts.query.${id}`, id);
  // A source the ledger carries (its lot) is read with it, and offers its fields there.
  const read = alertReadSources(form.sources);
  const fields = (catalog?.fields ?? []).filter(
    (field) => field.available && read.includes(field.source),
  );
  const fieldOptions = (ids: { id: string }[]): Option[] =>
    ids.map((field) => ({ value: field.id, label: fieldLabel(field.id) }));
  // A stored value the catalogue no longer offers still reads as itself.
  const withCurrent = (options: Option[], current: string, label: string): Option[] =>
    current && !options.some((option) => option.value === current)
      ? [{ value: current, label }, ...options]
      : options;

  const measureKind = kindOf(catalog, form.measureField);
  const functionOptions: Option[] = (
    measureKind ? (catalog?.functions[measureKind] ?? []) : []
  ).map((fn) => ({ value: fn, label: t(`alerts.builder.fn.${fn}`, fn) }));
  const maxGroupBy = catalog?.maxGroupBy ?? 6;

  // Unticking a source takes its fields out of the condition, so the rule never names a
  // field from a source it no longer reads.
  function toggleSource(sourceId: string, checked: boolean): void {
    const sources = checked
      ? [...form.sources, sourceId]
      : form.sources.filter((id) => id !== sourceId);
    const readAfter = alertReadSources(sources);
    const kept = (id: string): boolean => {
      const source = catalog?.fields.find((field) => field.id === id)?.source;
      return source === undefined || readAfter.includes(source);
    };
    set({
      sources,
      measureField: kept(form.measureField) ? form.measureField : '',
      groupBy: form.groupBy.filter(kept),
      context: form.context.filter(kept),
      filters: form.filters.filter((filter) => !filter.field || kept(filter.field)),
      windowField: form.windowField && !kept(form.windowField) ? '' : form.windowField,
    });
  }

  function updateFilter(index: number, patch: Partial<BuilderFilterForm>): void {
    set({
      filters: form.filters.map((filter, i) => (i === index ? { ...filter, ...patch } : filter)),
    });
  }

  function filterValueControl(filter: BuilderFilterForm, index: number): JSX.Element {
    const field = catalog?.fields.find((item) => item.id === filter.field);
    const label = t('alerts.builder.filter_value');
    if (field?.values && !LIST_OPERATORS.has(filter.op)) {
      return (
        <MudSelect
          size="medium"
          value={filter.value}
          aria-label={label}
          placeholder={t('alerts.policies.form.value_placeholder')}
          onMudChange={(e: CustomEvent<{ value: string }>) =>
            updateFilter(index, { value: e.detail.value })
          }
        >
          {field.values.map((value) => (
            <option key={value} value={value}>
              {t(valueLabelKey(filter.field, value), value)}
            </option>
          ))}
        </MudSelect>
      );
    }
    return (
      <MudTextInput
        size="md"
        value={filter.value}
        aria-label={label}
        placeholder={
          LIST_OPERATORS.has(filter.op)
            ? t('alerts.builder.filter_list_placeholder')
            : t('alerts.policies.form.value_placeholder')
        }
        onMudInput={(e: CustomEvent<{ value: string }>) =>
          updateFilter(index, { value: e.detail.value })
        }
      />
    );
  }

  const dateFields = fields.filter((field) => field.kind === 'date');

  return (
    <>
      <fieldset className="simsm-form-page__fieldset">
        <legend className="simsm-form-page__field-label">
          {t('alerts.builder.sources')}
          <span className="simsm-form-page__field-req" aria-hidden="true">
            {' '}
            *
          </span>
        </legend>
        <div className="simsm-alerts__channel-checks">
          {(catalog?.sources ?? [])
            .filter((source) => ALERT_CARRIED_SOURCES[source.id] === undefined)
            .map((source) => (
              <MudCheckbox
                key={source.id}
                size="md"
                checked={form.sources.includes(source.id)}
                onMudChange={(e: CustomEvent<{ checked: boolean }>) =>
                  toggleSource(source.id, e.detail.checked)
                }
              >
                <span slot="label">{t(`alerts.builder.source.${source.id}`, source.id)}</span>
              </MudCheckbox>
            ))}
        </div>
        <span className="simsm-alerts__meta">
          {errors.sources ? t(errors.sources) : t('alerts.builder.sources_hint')}
        </span>
      </fieldset>

      <FormRow columns={2}>
        <FormField
          label={t('alerts.builder.measure_field')}
          required
          error={errors.measure ? t(errors.measure) : undefined}
        >
          <MudSelect
            size="medium"
            value={form.measureField}
            placeholder={t('alerts.policies.form.value_placeholder')}
            onMudChange={(e: CustomEvent<{ value: string }>) => {
              const kind = kindOf(catalog, e.detail.value);
              const allowed = kind ? (catalog?.functions[kind] ?? []) : [];
              set({
                measureField: e.detail.value,
                measureFn: (allowed as readonly string[]).includes(form.measureFn)
                  ? form.measureFn
                  : (allowed[0] ?? ''),
              });
            }}
          >
            {withCurrent(
              fieldOptions(fields),
              form.measureField,
              fieldLabel(form.measureField),
            ).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </MudSelect>
        </FormField>
        <FormField label={t('alerts.builder.measure_fn')} required>
          <MudSelect
            size="medium"
            value={form.measureFn}
            onMudChange={(e: CustomEvent<{ value: string }>) => set({ measureFn: e.detail.value })}
          >
            {withCurrent(
              functionOptions,
              form.measureFn,
              t(`alerts.builder.fn.${form.measureFn}`, form.measureFn),
            ).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </MudSelect>
        </FormField>
      </FormRow>

      <FormRow columns={2}>
        <DimensionField
          label={t('alerts.builder.group_by')}
          hint={t('alerts.builder.group_by_hint', { max: maxGroupBy })}
          placeholder={t('alerts.builder.add_dimension')}
          options={fieldOptions(fields.filter((field) => field.groupable))}
          selected={form.groupBy}
          max={maxGroupBy}
          onChange={(groupBy) =>
            set({ groupBy, context: form.context.filter((id) => !groupBy.includes(id)) })
          }
        />
        <DimensionField
          label={t('alerts.builder.context')}
          hint={t('alerts.builder.context_hint')}
          placeholder={t('alerts.builder.add_dimension')}
          options={fieldOptions(fields.filter((field) => !form.groupBy.includes(field.id)))}
          selected={form.context}
          max={maxGroupBy}
          onChange={(context) => set({ context })}
        />
      </FormRow>

      <fieldset className="simsm-form-page__fieldset">
        <legend className="simsm-form-page__field-label">{t('alerts.builder.filters')}</legend>
        <div className="simsm-alerts__matchers">
          {form.filters.map((filter, index) => {
            const kind = kindOf(catalog, filter.field);
            const operators = kind ? (catalog?.operators[kind] ?? []) : [];
            return (
              <div key={index} className="simsm-alerts__matcher-row">
                <MudSelect
                  size="medium"
                  value={filter.field}
                  aria-label={t('alerts.builder.filter_field')}
                  placeholder={t('alerts.policies.form.value_placeholder')}
                  onMudChange={(e: CustomEvent<{ value: string }>) => {
                    const nextKind = kindOf(catalog, e.detail.value);
                    const allowed = nextKind ? (catalog?.operators[nextKind] ?? []) : [];
                    updateFilter(index, {
                      field: e.detail.value,
                      op: (allowed as readonly string[]).includes(filter.op)
                        ? filter.op
                        : (allowed[0] ?? 'eq'),
                      value: '',
                      numeric: nextKind === 'number',
                    });
                  }}
                >
                  {withCurrent(fieldOptions(fields), filter.field, fieldLabel(filter.field)).map(
                    (option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ),
                  )}
                </MudSelect>
                <MudSelect
                  size="medium"
                  value={filter.op}
                  aria-label={t('alerts.builder.filter_op')}
                  onMudChange={(e: CustomEvent<{ value: string }>) =>
                    updateFilter(index, {
                      op: e.detail.value,
                      value:
                        LIST_OPERATORS.has(e.detail.value) === LIST_OPERATORS.has(filter.op)
                          ? filter.value
                          : '',
                    })
                  }
                >
                  {withCurrent(
                    operators.map((op) => ({ value: op, label: t(`alerts.builder.op.${op}`, op) })),
                    filter.op,
                    t(`alerts.builder.op.${filter.op}`, filter.op),
                  ).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </MudSelect>
                {filterValueControl(filter, index)}
                <MudButton
                  shape="circular"
                  variant="destructive"
                  appearance="text"
                  size="sm"
                  label={t('alerts.builder.remove_filter')}
                  onClick={() => set({ filters: form.filters.filter((_, i) => i !== index) })}
                >
                  <MudIcon slot="icon" name="cross-small" size={16} />
                </MudButton>
              </div>
            );
          })}
          <MudButton
            shape="circular"
            variant="strict"
            appearance="text"
            size="sm"
            onClick={() =>
              set({
                filters: [...form.filters, { field: '', op: 'eq', value: '', numeric: false }],
              })
            }
          >
            <MudIcon slot="icon-start" name="plus-small" size={16} />
            {t('alerts.builder.add_filter')}
          </MudButton>
          <span className="simsm-alerts__meta">
            {errors.filters ? t(errors.filters) : t('alerts.builder.filters_hint')}
          </span>
        </div>
      </fieldset>

      <FormRow columns={3}>
        <FormField
          label={t('alerts.builder.window_amount')}
          hint={t('alerts.builder.window_hint')}
          error={errors.window ? t(errors.window) : undefined}
        >
          <MudTextInput
            size="md"
            inputMode="numeric"
            value={form.windowAmount}
            onMudInput={(e: CustomEvent<{ value: string }>) =>
              set({ windowAmount: e.detail.value })
            }
          />
        </FormField>
        <FormField label={t('alerts.builder.window_unit')}>
          <MudSelect
            size="medium"
            value={form.windowUnit}
            onMudChange={(e: CustomEvent<{ value: string }>) =>
              set({ windowUnit: e.detail.value === 'days' ? 'days' : 'hours' })
            }
          >
            {(['hours', 'days'] as const).map((unit) => (
              <option key={unit} value={unit}>
                {t(`alerts.builder.unit.${unit}`)}
              </option>
            ))}
          </MudSelect>
        </FormField>
        <FormField label={t('alerts.builder.window_field')}>
          <MudSelect
            size="medium"
            value={form.windowField}
            onMudChange={(e: CustomEvent<{ value: string }>) =>
              set({ windowField: e.detail.value })
            }
          >
            <option value="">{t('alerts.builder.window_field_default')}</option>
            {withCurrent(
              fieldOptions(dateFields),
              form.windowField,
              fieldLabel(form.windowField),
            ).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </MudSelect>
        </FormField>
      </FormRow>

      <FormRow columns={2}>
        <FormField label={t('alerts.builder.comparison_op')} required>
          <MudSelect
            size="medium"
            value={form.comparisonOp}
            onMudChange={(e: CustomEvent<{ value: string }>) =>
              set({ comparisonOp: e.detail.value })
            }
          >
            {(catalog?.comparisons ?? [form.comparisonOp]).map((op) => (
              <option key={op} value={op}>
                {t(`alerts.builder.op.${op}`, op)}
              </option>
            ))}
          </MudSelect>
        </FormField>
        <FormField
          label={t('alerts.builder.comparison_value')}
          required
          error={errors.comparison ? t(errors.comparison) : undefined}
        >
          <MudTextInput
            size="md"
            inputMode="decimal"
            value={form.comparisonValue}
            onMudInput={(e: CustomEvent<{ value: string }>) =>
              set({ comparisonValue: e.detail.value })
            }
          />
        </FormField>
      </FormRow>
    </>
  );
}
